import React, { useState, useCallback } from 'react';
import { Form, Input, Button, notification } from 'antd';
import { useHistory } from 'react-router-dom';
import ajaxService from '../../services/ajax-service';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import AuthLayout from './AuthLayout';

const ResetPassword = () => {
    const history = useHistory();

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const showSuccess = useCallback((message) => {
        notification.open({
            message,
            icon: <CheckCircleOutlined style={{ color: 'var(--primary-purple)' }} />,
            duration: 3,
        });
    }, []);

    const showError = useCallback((message) => {
        notification.open({
            message,
            icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} />,
            duration: 3,
        });
    }, []);

    const onFinish = useCallback(async (values) => {
        try {
            setLoading(true);
            const email = localStorage.getItem('forgotPasswordEmail');
            if (!email) {
                showError('Email not found. Please start the password reset process again.');
                return;
            }
            const data = { ...values, email };
            const response = await ajaxService.post('/reset-password', data);

            if (response.success) {
                localStorage.removeItem('forgotPasswordEmail');
                history.push({ pathname: '/login', email: response.data.email });
                showSuccess('Password has been updated successfully.');
            } else {
                showError(response.data?.message || 'Email does not exist!');
            }
        } catch (error) {
            console.error('Reset password error:', error);
            showError('An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [history, showSuccess, showError]);

    const onFinishFailed = useCallback((errorInfo) => {
        console.log('Form validation failed:', errorInfo);
        if (errorInfo.errorFields.length > 0) {
            showError('Please check your input and try again.');
        }
    }, [showError]);

    const formContent = (props) => {
        const { formItemStyle, buttonItemStyle, textSize, formSize, spacing, fieldGap, inputHeight, formWidth, buttonHeight, buttonFont } = props;
        return (
            <Form
                form={form}
                name="basic"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                layout="vertical"
                size={formSize}
                autoComplete="on"
                aria-label="Reset password form"
                style={{ 
                    width: formWidth,
                    margin: '0 auto',
                }}
            >
                <Form.Item
                    name="password"
                    className='login-form-item'
                    rules={[{ required: true, message: 'Please input your password!' }]}
                    hasFeedback
                    style={formItemStyle}
                >
                    <Input.Password 
                        placeholder="Enter new password here" 
                        allowClear
                        autoComplete="new-password"
                        style={{ 
                            borderRadius: 'var(--radius-lg)', 
                            height: inputHeight,
                            background: 'var(--dark-bg-card)',
                            border: '1px solid var(--border-light)',
                            color: 'var(--text-white)',
                        }} 
                    />
                </Form.Item>

                <Form.Item
                    name="confirmed_password"
                    className='login-form-item'
                    dependencies={['password']}
                    rules={[
                        {
                            required: true,
                            message: 'Please confirm your password!',
                        },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('The new password that you entered do not match!'));
                            },
                        }),
                    ]}
                    hasFeedback
                    style={formItemStyle}
                >
                    <Input.Password 
                        placeholder="Confirm new password here" 
                        allowClear
                        autoComplete="new-password"
                        style={{ 
                            borderRadius: 'var(--radius-lg)', 
                            height: inputHeight,
                            background: 'var(--dark-bg-card)',
                            border: '1px solid var(--border-light)',
                            color: 'var(--text-white)',
                        }} 
                    />
                </Form.Item>

                <Form.Item style={{...buttonItemStyle, marginTop: spacing }}>
                    <Button 
                        loading={loading} 
                        type="primary" 
                        htmlType="submit" 
                        block
                        size={formSize}
                        style={{ 
                            width: formWidth,
                            background: 'var(--gradient-primary)',
                            border: 'none',
                            borderRadius: 'var(--radius-lg)',
                            height: buttonHeight,
                            fontWeight: 600,
                            fontSize: buttonFont,
                            marginTop:spacing,
                            boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.3)',
                        }}
                        aria-label="Confirm password reset"
                    >
                        {loading ? 'Confirming...' : 'Confirm'}
                    </Button>
                </Form.Item>
            </Form>
        );
    };

    return (
        <AuthLayout
            title="Reset Your Password"
            subtitle="Enter your new password"
            formContent={formContent}
            onError={showError}
            onSuccess={showSuccess}
        />
    );
};

export default React.memo(ResetPassword);